/**
 * The three things a live pass shows about its wait: how long it has been going,
 * how long one like it usually takes here, and what it has searched for and
 * opened so far. Shared by the feed's live message (`ui/Feed.tsx`) and the line
 * over the field (`Thoughtstream` in `ui/Overlay.tsx`), so the two cannot print
 * one pass's wait two ways. The figures and the rules behind them are
 * `core/passTime.ts`; the trail's rows are `core/searchTrail.ts`.
 *
 * None of it is a live region, and the clock is hidden from the tree: a region
 * over a ticking figure queues one announcement a second (D216, the run strip's
 * countdown). What a screen reader hears of a pass is its start and its landing,
 * from the announcer.
 */

import { useEffect, useMemo, useState } from 'react'
import {
  elapsedWords,
  passTimeKind,
  usualBasis,
  usualFor,
  usualSpoken,
  usualWords,
  type Usual,
} from '../core/passTime'
import { earlierWords, trailRows } from '../core/searchTrail'
import { useStore } from '../core/store'
import type { Op } from '../core/types'

/** The kind this pass's seconds are filed under — an executable test apart from
 *  the Lab's analyses (D289). Read, not subscribed: it is fixed once a pass starts. */
const kindOf = (op: Op) => passTimeKind(useStore.getState().session, op)

/**
 * Seconds since `since`, counted up in whole seconds.
 *
 * Its own one-second timer, set to fire on each whole second of the pass and
 * cleared when the clock unmounts — which is when the pass lands, because only a
 * live pass renders one. A state write re-renders this span and nothing else:
 * not the message around it, and not the canvas, which wakes on store writes
 * (D191) and this is not one. Text, so it ticks under reduced motion too.
 */
export function PassClock({ since, className }: { since: number; className?: string }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    // To the next whole second of the pass, so the digits turn evenly rather
    // than drifting a few milliseconds late on every tick.
    const wait = () => 1000 - (Math.max(0, Date.now() - since) % 1000) + 4
    const tick = () => {
      setNow(Date.now())
      timer = setTimeout(tick, wait())
    }
    let timer = setTimeout(tick, wait())
    return () => clearTimeout(timer)
  }, [since])
  return (
    <span className={className ? `pass-clock ${className}` : 'pass-clock'} aria-hidden="true">
      {elapsedWords(now - since)}
    </span>
  )
}

/**
 * "Usually" for this op, read once per pass: the record changes only when a pass
 * lands, and this op's own landing ends the line.
 */
export function useUsual(op: Op | null): Usual | null {
  const id = op?.id
  return useMemo(
    () => (op ? usualFor(kindOf(op), op.model) : null),
    // Keyed on the pass, not the object: the op is rewritten as its nodes arrive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id],
  )
}

/**
 * "usually ~50s here", with what it is made of on its title. Printed as a figure
 * and read as words; never counts down, never changes colour, and says nothing
 * more when a pass runs past it.
 */
export function UsualNote({ usual, op, className }: { usual: Usual; op: Op; className?: string }) {
  return (
    <span
      className={className ? `pass-usual ${className}` : 'pass-usual'}
      title={op.model ? usualBasis(usual, kindOf(op), op.model) : undefined}
    >
      <span aria-hidden="true">{usualWords(usual)}</span>
      <span className="sr-only">{usualSpoken(usual)}</span>
    </span>
  )
}

/**
 * The live pass's search trail: the last few steps, oldest first, the newest in
 * the action's own ink and the rest stepped back, and one line counting any
 * before them. Nothing at all before the first step.
 *
 * `quiet` when a wait line stands under it: the newest step is then not what the
 * pass is doing, so it steps back with the rest.
 */
export function SearchTrail({
  trail,
  quiet = false,
}: {
  trail: readonly string[]
  quiet?: boolean
}) {
  if (trail.length === 0) return null
  const { earlier, rows } = trailRows(trail)
  return (
    <ol className="feed-trail">
      {earlier > 0 && <li className="feed-trail-earlier">{earlierWords(earlier)}</li>}
      {rows.map((r, i) => (
        <li key={i} data-newest={(!quiet && i === rows.length - 1) || undefined}>
          {r.line}
          {r.times > 1 && <span className="feed-trail-times"> ×{r.times}</span>}
        </li>
      ))}
    </ol>
  )
}
